import React, { useState } from 'react';
import * as T from '../constants/disclosureTemplates';
import Redline from './Redline';

// ── Shared styles (match YieldCalculator) ────────────────────
const sHdr = { background: 'var(--ribbon-bg)', color: 'var(--ribbon-text)', padding: '6px 14px',
  fontSize: '12px', fontWeight: '600', letterSpacing: '0.05em', textTransform: 'uppercase',
  borderRadius: '4px', marginBottom: '12px', marginTop: '4px' };
const sInput = { width: '100%', padding: '7px 10px', border: '1px solid var(--border-color)',
  borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box', outline: 'none',
  background: 'var(--input-bg)', color: 'var(--text-primary)', fontFamily: 'var(--font-body)' };
const sLabel = { display: 'block', fontSize: '11px', fontWeight: '600',
  color: 'var(--text-secondary)', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.04em' };
const sBtn = { padding: '8px 18px', borderRadius: '4px', border: 'none', cursor: 'pointer',
  fontSize: '13px', fontWeight: '600', fontFamily: 'var(--font-body)', transition: 'var(--transition-fast)' };
const sBtnPrimary = { ...sBtn, background: 'var(--accent-color)', color: '#fff' };

const typeBtn = (active) => ({
  ...sBtn, padding: '10px 20px',
  background: active ? 'var(--accent-color)' : 'var(--bg-tertiary)',
  color: active ? '#fff' : 'var(--text-primary)',
  border: active ? 'none' : '1px solid var(--border-color)',
});

// ── Placeholder substitution ─────────────────────────────────
function fill(template, fields) {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => fields[key] || `{{${key}}}`);
}

// Resolve [NC: ...] [SC: ...] markers based on selected state
function resolveState(text, state) {
  if (state === 'NC') {
    return text.replace(/\[NC:\s*([^\]]+)\]\s*\[SC:\s*[^\]]+\]/g, '$1');
  }
  return text.replace(/\[NC:\s*[^\]]+\]\s*\[SC:\s*([^\]]+)\]/g, '$1');
}

// ── Singular bond post-processing ────────────────────────────
// If the user typed a singular bond term (e.g. "Bond" not "Bonds"),
// fix subject-verb agreement and plural references automatically.
function singularize(paras, bondsField) {
  if (!bondsField || bondsField.trimEnd().endsWith('s')) return paras;
  return paras.map(para => {
    if (para.style === 'heading') return para;
    let t = para.text;
    t = t.replace(/are being sold at initial offering prices that are less than/g,
                   'is being sold at an initial offering price that is less than');
    t = t.replace(/are being sold at initial offering prices which are in excess/g,
                   'is being sold at an initial offering price which is in excess');
    t = t.replace(/OID Bonds/g, 'OID Bond');
    t = t.replace(/Premium Bonds/g, 'Premium Bond');
    t = t.replace(/the Bonds are/g, 'the Bond is');
    t = t.replace(/such Bonds/g, 'such Bond');
    return { ...para, text: t };
  });
}

// Derive singular BOND from BONDS (e.g. "2026 Bonds" → "2026 Bond")
function withSingularBond(f) {
  if (f.BOND || !f.BONDS) return f;
  const b = f.BONDS.trimEnd();
  return { ...f, BOND: b.endsWith('s') ? b.slice(0, -1) : b };
}

// ── Build paragraph arrays ───────────────────────────────────

// Builders return { cover_paragraphs, body_paragraphs }.
// Generate mode flattens via flattenForGenerate() below, so the generated .docx
// keeps its existing shape: cover paragraphs, then "TAX TREATMENT" heading, then body.

function buildGovernmental(f) {
  f = withSingularBond(f);
  const p = (text, style = 'body') => ({ text: resolveState(fill(text, f), f.STATE), style });
  const cover = [];
  const body = [];
  cover.push(p(T.GOV_COVER));
  if (f.BQ) cover.push(p(T.GOV_BQ_COVER));
  body.push(p(T.GOV_P1));
  if (f.BQ) body.push(p(T.GOV_BQ_TAX));
  body.push(p(T.GOV_P2));
  body.push(p(T.GOV_P3));
  body.push(p(T.GOV_P4));
  body.push(p(T.GOV_P5));
  body.push(p(T.GOV_P6));
  body.push(p(T.GOV_P7));
  body.push(p(T.GOV_P8));
  if (f.OID) {
    body.push(p(T.OID_1, 'body_bold_intro'));
    body.push(p(T.OID_2));
    body.push(p(T.OID_3));
    body.push(p(T.OID_4));
  }
  if (f.OIP) body.push(p(T.GOV_OIP, 'body_bold_intro'));
  return {
    cover_paragraphs: singularize(cover, f.BONDS),
    body_paragraphs: singularize(body, f.BONDS),
  };
}

function build501c3(f) {
  f = withSingularBond(f);
  const is3 = !!f.BORROWER;
  const p = (text, style = 'body') => ({ text: fill(text, f), style });
  const cover = [p(is3 ? T.C3_COVER_3PARTY : T.C3_COVER_2PARTY)];
  const body = [];
  body.push(p(T.C3_P1));
  body.push(p(f.STATE === 'SC' ? T.C3_STATE_SC : T.C3_STATE_NC));
  body.push(p(is3 ? T.C3_P2_3PARTY : T.C3_P2_2PARTY));
  if (f.COUNSEL_TYPE === 'full' && f.BORROWER_COUNSEL) {
    body.push(p(T.C3_COUNSEL_FULL));
  } else if (f.COUNSEL_TYPE === 'c3only' && f.BORROWER_COUNSEL) {
    body.push(p(T.C3_COUNSEL_C3ONLY));
  }
  body.push(p(is3 ? T.C3_P3_3PARTY : T.C3_P3_2PARTY));
  body.push(p(is3 ? T.C3_P4_3PARTY : T.C3_P4_2PARTY));
  body.push(p(T.C3_P5));
  body.push(p(T.C3_P6));
  body.push(p(T.C3_P7));
  body.push(p(T.C3_P8));
  body.push(p(T.C3_P9));
  if (f.OID) {
    body.push(p(T.OID_1, 'body_bold_intro'));
    body.push(p(T.OID_2));
    body.push(p(T.OID_3));
    body.push(p(T.OID_4));
  }
  if (f.OIP) body.push(p(T.GOV_OIP, 'body_bold_intro'));
  return {
    cover_paragraphs: singularize(cover, f.BONDS),
    body_paragraphs: singularize(body, f.BONDS),
  };
}

function buildHousing(f) {
  f = withSingularBond(f);
  const hasCo = !!f.CO_BORROWER;
  const makeText = (template) => {
    let t = fill(template, f);
    if (hasCo) {
      const I = f.ISSUER;
      const B = f.BORROWER;
      const CB = f.CO_BORROWER;
      t = t.replaceAll(`neither the ${I} nor the ${B}`, `neither the ${I}, the ${B} nor the ${CB}`);
      t = t.replaceAll(`the ${I} or the ${B}`, `the ${I}, the ${B} or the ${CB}`);
      t = t.replaceAll(`the ${I} and the ${B}`, `the ${I}, the ${B} and the ${CB}`);
      t = t.replaceAll(`The ${I} and the ${B}`, `The ${I}, the ${B} and the ${CB}`);
    }
    return t;
  };
  const p = (text, style = 'body') => ({ text: makeText(text), style });
  const cover = [p(T.HOUSING_COVER)];
  const body = [];
  body.push({ text: makeText(T.HOUSING_P1), style: 'body' });
  body.push(p(T.HOUSING_P2));
  body.push(p(T.HOUSING_P3));
  body.push(p(T.HOUSING_P4));
  body.push(p(T.HOUSING_P5));
  body.push(p(T.HOUSING_P6));
  body.push(p(T.HOUSING_P7));
  body.push(p(T.HOUSING_P8));
  body.push(p(T.HOUSING_P9));
  if (f.OID) {
    body.push(p(T.OID_1, 'body_bold_intro'));
    body.push(p(T.OID_2));
    body.push(p(T.OID_3));
    body.push(p(T.OID_4));
  }
  if (f.OIP) body.push(p(T.HOUSING_OIP, 'body_bold_intro'));
  return {
    cover_paragraphs: singularize(cover, f.BONDS),
    body_paragraphs: singularize(body, f.BONDS),
  };
}

// Flatten {cover_paragraphs, body_paragraphs} back to the legacy flat array
// that the /api/generate_disclosure endpoint still consumes.
function flattenForGenerate({ cover_paragraphs, body_paragraphs }) {
  return [
    ...cover_paragraphs,
    { text: 'TAX TREATMENT', style: 'heading' },
    ...body_paragraphs,
  ];
}

// ── Component ────────────────────────────────────────────────
const TaxDisclosure = () => {
  const [mode, setMode] = useState('generate'); // 'generate' | 'check'
  const [disclosureType, setDisclosureType] = useState('governmental');
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');

  // Checker state
  const [inputMode, setInputMode] = useState('pasted'); // 'pasted' | 'extracted'
  const [uploadedFile, setUploadedFile] = useState(null);
  const [pasteCover, setPasteCover] = useState('');
  const [pasteBody, setPasteBody] = useState('');
  const [checking, setChecking] = useState(false);
  const [redline, setRedline] = useState(null);
  const [redlineHeader, setRedlineHeader] = useState('');
  const [dragOver, setDragOver] = useState(false);

  // Governmental fields
  const [govIssuer, setGovIssuer] = useState('');
  const [govBonds, setGovBonds] = useState('');
  const [govState, setGovState] = useState('NC');
  const [govBQ, setGovBQ] = useState(false);
  const [govOID, setGovOID] = useState(false);
  const [govOIP, setGovOIP] = useState(false);

  // 501(c)(3) fields
  const [c3Authority, setC3Authority] = useState('');
  const [c3Bonds, setC3Bonds] = useState('');
  const [c3Corporation, setC3Corporation] = useState('');
  const [c3Borrower, setC3Borrower] = useState('');
  const [c3BorrowerCounsel, setC3BorrowerCounsel] = useState('');
  const [c3State, setC3State] = useState('NC');
  const [c3CounselType, setC3CounselType] = useState('full');
  const [c3OID, setC3OID] = useState(false);
  const [c3OIP, setC3OIP] = useState(false);

  // Housing fields
  const [hIssuer, setHIssuer] = useState('');
  const [hBorrower, setHBorrower] = useState('');
  const [hCoBorrower, setHCoBorrower] = useState('');
  const [hBonds, setHBonds] = useState('');
  const [hOID, setHOID] = useState(false);
  const [hOIP, setHOIP] = useState(false);

  const generate = async () => {
    setError('');
    let split, filename;
    try {
      if (disclosureType === 'governmental') {
        if (!govIssuer || !govBonds) { setError('Issuer and Bond Name are required.'); return; }
        split = buildGovernmental({ ISSUER: govIssuer, BONDS: govBonds, STATE: govState, BQ: govBQ, OID: govOID, OIP: govOIP });
        filename = 'Tax_Disclosure_Governmental.docx';
      } else if (disclosureType === '501c3') {
        if (!c3Authority || !c3Bonds || !c3Corporation) { setError('Authority, Bond Name, and 501(c)(3) Entity are required.'); return; }
        split = build501c3({
          AUTHORITY: c3Authority, BONDS: c3Bonds, CORPORATION: c3Corporation,
          BORROWER: c3Borrower, BORROWER_COUNSEL: c3BorrowerCounsel,
          STATE: c3State, COUNSEL_TYPE: c3CounselType, OID: c3OID, OIP: c3OIP,
        });
        filename = 'Tax_Disclosure_501c3.docx';
      } else {
        if (!hIssuer || !hBorrower || !hBonds) { setError('Issuer, Borrower, and Bond Name are required.'); return; }
        split = buildHousing({ ISSUER: hIssuer, BORROWER: hBorrower, CO_BORROWER: hCoBorrower, BONDS: hBonds, OID: hOID, OIP: hOIP });
        filename = 'Tax_Disclosure_142d_Housing.docx';
      }
      const paras = flattenForGenerate(split);

      setGenerating(true);
      const response = await fetch('/api/generate_disclosure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paragraphs: paras, filename }),
      });
      if (!response.ok) throw new Error(await response.text() || `Server error ${response.status}`);

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click();
      window.URL.revokeObjectURL(url); document.body.removeChild(a);
    } catch (e) {
      setError(e.message);
    } finally {
      setGenerating(false);
    }
  };

  // ── Build {cover_paragraphs, body_paragraphs} for current form state ─────
  const getCurrentSplit = () => {
    if (disclosureType === 'governmental') {
      if (!govIssuer || !govBonds) return null;
      return buildGovernmental({ ISSUER: govIssuer, BONDS: govBonds, STATE: govState, BQ: govBQ, OID: govOID, OIP: govOIP });
    } else if (disclosureType === '501c3') {
      if (!c3Authority || !c3Bonds || !c3Corporation) return null;
      return build501c3({ AUTHORITY: c3Authority, BONDS: c3Bonds, CORPORATION: c3Corporation,
        BORROWER: c3Borrower, BORROWER_COUNSEL: c3BorrowerCounsel, STATE: c3State, COUNSEL_TYPE: c3CounselType, OID: c3OID, OIP: c3OIP });
    } else {
      if (!hIssuer || !hBorrower || !hBonds) return null;
      return buildHousing({ ISSUER: hIssuer, BORROWER: hBorrower, CO_BORROWER: hCoBorrower, BONDS: hBonds, OID: hOID, OIP: hOIP });
    }
  };

  const buildVariations = () => {
    if (disclosureType === 'governmental') {
      return { BQ: !!govBQ, OID: !!govOID, OIP: !!govOIP };
    }
    if (disclosureType === '501c3') {
      return { COUNSEL_TYPE: c3CounselType, OID: !!c3OID, OIP: !!c3OIP, three_party: !!c3Borrower };
    }
    return { OID: !!hOID, OIP: !!hOIP, co_borrower: !!hCoBorrower };
  };

  const buildHeaderLabel = () => {
    const labelByType = { governmental: 'Governmental', '501c3': '501(c)(3)', housing: '§142(d) Housing' };
    const state = disclosureType === 'governmental' ? govState
      : disclosureType === '501c3' ? c3State
      : 'SC';
    const variations = buildVariations();
    const flags = Object.entries(variations)
      .filter(([, v]) => v === true || (typeof v === 'string' && v))
      .map(([k, v]) => typeof v === 'string' ? `${k}=${v}` : k)
      .join(', ');
    return `Redline: ${labelByType[disclosureType]} — ${state}${flags ? ` — ${flags}` : ''}`;
  };

  const checkDisclosure = async () => {
    setError('');
    setRedline(null);

    const split = getCurrentSplit();
    if (!split) { setError('Fill in the required deal parameters first.'); return; }

    if (inputMode === 'extracted' && !uploadedFile) {
      setError('Please upload a .docx file, or switch to paste mode.');
      return;
    }
    if (inputMode === 'pasted' && !pasteCover.trim() && !pasteBody.trim()) {
      setError('Paste either the cover paragraph or the body tax section (or both).');
      return;
    }

    setChecking(true);

    try {
      const state = disclosureType === 'governmental' ? govState
        : disclosureType === '501c3' ? c3State
        : 'SC';
      const formData = new FormData();
      formData.append('input_mode', inputMode);
      formData.append('baseline_cover_paragraphs', JSON.stringify(split.cover_paragraphs));
      formData.append('baseline_body_paragraphs', JSON.stringify(split.body_paragraphs));
      formData.append('deal_type', disclosureType);
      formData.append('state', state);
      formData.append('variations', JSON.stringify(buildVariations()));
      if (inputMode === 'pasted') {
        formData.append('proposed_cover_text', pasteCover);
        formData.append('proposed_body_text', pasteBody);
      } else {
        formData.append('file', uploadedFile);
      }

      const resp = await fetch('/api/tax-disclosure/check', { method: 'POST', body: formData });
      if (!resp.ok) {
        let detail = `Server error ${resp.status}`;
        try {
          const body = await resp.json();
          if (body?.detail) detail = body.detail;
        } catch (_) {
          try { detail = await resp.text() || detail; } catch (__) { /* ignore */ }
        }
        throw new Error(detail);
      }

      const data = await resp.json();
      setRedlineHeader(buildHeaderLabel());
      setRedline(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setChecking(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file && file.name.endsWith('.docx')) setUploadedFile(file);
    else setError('Please upload a .docx file');
  };

  const handleFileInput = (e) => {
    const file = e.target.files?.[0];
    if (file) setUploadedFile(file);
  };

  // ── Render helpers ────────────────────────────────────────
  const radioGroup = (name, value, onChange, options) => (
    <div style={{ display: 'flex', gap: '16px', marginTop: '4px' }}>
      {options.map(opt => (
        <label key={opt.value} style={{ display: 'flex', alignItems: 'center', gap: '5px',
          fontSize: '13px', color: 'var(--text-primary)', cursor: 'pointer' }}>
          <input type="radio" name={name} value={opt.value} checked={value === opt.value}
            onChange={() => onChange(opt.value)} style={{ accentColor: 'var(--accent-color)' }} />
          {opt.label}
        </label>
      ))}
    </div>
  );

  const checkbox = (label, checked, onChange) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px',
      color: 'var(--text-primary)', cursor: 'pointer' }}>
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)}
        style={{ accentColor: 'var(--accent-color)' }} />
      {label}
    </label>
  );

  const field = (label, value, onChange, placeholder = '') => (
    <div style={{ flex: '1 1 280px' }}>
      <label style={sLabel}>{label}</label>
      <input type="text" value={value} onChange={e => onChange(e.target.value)}
        placeholder={placeholder} style={sInput} />
    </div>
  );

  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '24px 32px', fontFamily: 'var(--font-body)',
      maxWidth: '800px', margin: '0 auto', width: '100%' }}>

      <h2 style={{ fontFamily: 'var(--font-reading)', fontSize: '22px', fontWeight: '600',
        color: 'var(--text-primary)', marginBottom: '4px' }}>
        Tax Disclosure {mode === 'generate' ? 'Generator' : 'Checker'}
      </h2>
      <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '12px' }}>
        {mode === 'generate' ? 'Generates offering document tax disclosure language as a Word document'
          : 'Compare an uploaded OS/POS disclosure against Parker Poe standard form'}
      </p>

      {/* ── Mode toggle ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
        <button style={typeBtn(mode === 'generate')} onClick={() => { setMode('generate'); setError(''); }}>Generate Disclosure</button>
        <button style={typeBtn(mode === 'check')} onClick={() => { setMode('check'); setError(''); }}>Check Disclosure</button>
      </div>

      {/* ── Type selector ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <button style={typeBtn(disclosureType === 'governmental')} onClick={() => setDisclosureType('governmental')}>Governmental</button>
        <button style={typeBtn(disclosureType === '501c3')} onClick={() => setDisclosureType('501c3')}>Qualified 501(c)(3)</button>
        <button style={typeBtn(disclosureType === 'housing')} onClick={() => setDisclosureType('housing')}>&sect;142(d) Housing</button>
      </div>

      {/* ── GOVERNMENTAL ──────────────────────────────────── */}
      {disclosureType === 'governmental' && (
        <>
          <div style={sHdr}>Governmental Bond Details</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field('Capitalized term for Issuer', govIssuer, setGovIssuer, 'e.g. City, County, Authority, Issuer')}
            {field('Capitalized term for Bonds', govBonds, setGovBonds, 'e.g. Bonds, Series 2026 Bonds')}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={sLabel}>State</label>
            {radioGroup('govState', govState, setGovState, [
              { value: 'NC', label: 'North Carolina' }, { value: 'SC', label: 'South Carolina' },
            ])}
          </div>
          <div style={{ marginBottom: '12px' }}>
            {checkbox('Designated as bank qualified (§265(b)(3))', govBQ, setGovBQ)}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
            {checkbox('Original Issue Discount', govOID, setGovOID)}
            {checkbox('Original Issue Premium', govOIP, setGovOIP)}
          </div>
        </>
      )}

      {/* ── 501(c)(3) ────────────────────────────────────── */}
      {disclosureType === '501c3' && (
        <>
          <div style={sHdr}>501(c)(3) Bond Details</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field('Capitalized term for Authority', c3Authority, setC3Authority, 'e.g. Authority, Issuer')}
            {field('Capitalized term for Bonds', c3Bonds, setC3Bonds, 'e.g. Bonds, Series 2026 Bonds')}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field('Capitalized term for 501(c)(3) entity', c3Corporation, setC3Corporation, 'e.g. Corporation, Organization')}
            {field('Capitalized term for Borrower (leave blank if same as above)', c3Borrower, setC3Borrower, 'e.g. LLC Borrower, Borrower')}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field("Borrower's counsel firm name", c3BorrowerCounsel, setC3BorrowerCounsel, 'e.g. Smith & Jones LLP')}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={sLabel}>State</label>
            {radioGroup('c3State', c3State, setC3State, [
              { value: 'NC', label: 'North Carolina' }, { value: 'SC', label: 'South Carolina' },
            ])}
          </div>
          <div style={{ marginBottom: '12px' }}>
            <label style={sLabel}>Borrower Counsel Paragraph</label>
            {radioGroup('c3Counsel', c3CounselType, setC3CounselType, [
              { value: 'full', label: '501(c)(3) + 513(a) opinion' },
              { value: 'c3only', label: '501(c)(3) only' },
              { value: 'none', label: 'None' },
            ])}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
            {checkbox('Original Issue Discount', c3OID, setC3OID)}
            {checkbox('Original Issue Premium', c3OIP, setC3OIP)}
          </div>
        </>
      )}

      {/* ── §142(d) HOUSING ───────────────────────────────── */}
      {disclosureType === 'housing' && (
        <>
          <div style={sHdr}>&sect;142(d) Housing Bond Details (SC Only)</div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field('Capitalized term for Issuer', hIssuer, setHIssuer, 'e.g. Issuer, Authority')}
            {field('Capitalized term for Borrower', hBorrower, setHBorrower, 'e.g. Borrower, Company')}
          </div>
          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' }}>
            {field('Capitalized term for Co-Borrower (leave blank if none)', hCoBorrower, setHCoBorrower, 'e.g. Co-Borrower, Guarantor')}
            {field('Capitalized term for Bonds', hBonds, setHBonds, 'e.g. Bonds, Series 2026 Bonds')}
          </div>
          <div style={{ display: 'flex', gap: '20px', marginBottom: '16px' }}>
            {checkbox('Original Issue Discount', hOID, setHOID)}
            {checkbox('Original Issue Premium', hOIP, setHOIP)}
          </div>
        </>
      )}

      {/* ── Generate mode button ──────────────────────────── */}
      {mode === 'generate' && (
        <button onClick={generate} disabled={generating} style={{ ...sBtnPrimary, opacity: generating ? 0.6 : 1 }}>
          {generating ? 'Generating...' : 'Generate Disclosure (.docx)'}
        </button>
      )}

      {/* ── Check mode UI ─────────────────────────────────── */}
      {mode === 'check' && (
        <div style={{ marginTop: '8px' }}>
          <div style={sHdr}>Proposed Disclosure Source</div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
            <button style={typeBtn(inputMode === 'pasted')} onClick={() => setInputMode('pasted')}>
              Paste tax sections (fast)
            </button>
            <button style={typeBtn(inputMode === 'extracted')} onClick={() => setInputMode('extracted')}>
              Upload full OS (extraction)
            </button>
          </div>

          {inputMode === 'pasted' && (
            <>
              <div style={{ marginBottom: '12px' }}>
                <label style={sLabel}>Cover-page opinion paragraph</label>
                <textarea
                  rows={10}
                  value={pasteCover}
                  onChange={e => setPasteCover(e.target.value)}
                  placeholder="Paste the single tax paragraph from the cover page (typically begins with “In the opinion of …” and ends with “See TAX TREATMENT herein.”)"
                  style={{ ...sInput, fontFamily: 'var(--font-body)', lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>
              <div style={{ marginBottom: '16px' }}>
                <label style={sLabel}>Body Tax Treatment section</label>
                <textarea
                  rows={25}
                  value={pasteBody}
                  onChange={e => setPasteBody(e.target.value)}
                  placeholder="Paste the full Tax Treatment / Tax Matters section from the body of the OS (without the heading). Separate paragraphs with a blank line."
                  style={{ ...sInput, fontFamily: 'var(--font-body)', lineHeight: 1.5, resize: 'vertical' }}
                />
              </div>
            </>
          )}

          {inputMode === 'extracted' && (
            <div
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleDrop}
              style={{
                border: `2px dashed ${dragOver ? 'var(--accent-color)' : 'var(--border-color)'}`,
                borderRadius: '8px', padding: '24px', textAlign: 'center',
                marginBottom: '16px', cursor: 'pointer',
                background: dragOver ? 'var(--bg-tertiary)' : 'transparent',
                transition: 'var(--transition-fast)',
              }}
              onClick={() => document.getElementById('disclosure-file-input')?.click()}
            >
              <input id="disclosure-file-input" type="file" accept=".docx"
                style={{ display: 'none' }} onChange={handleFileInput} />
              {uploadedFile ? (
                <div style={{ fontSize: '13px', color: 'var(--text-primary)' }}>
                  <strong>{uploadedFile.name}</strong> ({(uploadedFile.size / 1024).toFixed(0)} KB)
                  <div style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                    Click or drag to replace
                  </div>
                </div>
              ) : (
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                  Drop OS/POS Word document here (.docx)<br />
                  <span style={{ fontSize: '11px' }}>or click to browse</span>
                </div>
              )}
            </div>
          )}

          <button onClick={checkDisclosure} disabled={checking}
            style={{ ...sBtnPrimary, opacity: checking ? 0.6 : 1 }}>
            {checking ? 'Analyzing…' : 'Review Disclosure'}
          </button>

          {checking && (
            <div style={{ marginTop: '12px', fontSize: '12px', color: 'var(--text-secondary)',
              display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ display: 'inline-block', width: '14px', height: '14px',
                border: '2px solid var(--accent-color)', borderTopColor: 'transparent',
                borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
              Analyzing and aligning… usually under 30 seconds.
            </div>
          )}

          {redline && (
            <div style={{ marginTop: '16px' }}>
              <Redline result={redline.cover} header={`${redlineHeader} — Cover paragraph`} />
              <div style={{ margin: '20px 0', borderTop: '2px dashed var(--border-color)' }} />
              <Redline result={redline.body} header={`${redlineHeader} — Body Tax Treatment`} />

              {redline.global_diagnostics && (
                <details style={{
                  marginTop: '12px', fontSize: '11px', color: 'var(--text-secondary)',
                  border: '1px solid var(--border-color)', borderRadius: '4px',
                  padding: '6px 10px', background: 'var(--bg-tertiary)',
                }}>
                  <summary style={{ cursor: 'pointer', userSelect: 'none', fontWeight: 600 }}>
                    Global diagnostics
                  </summary>
                  <div style={{ marginTop: '8px', fontFamily: 'var(--font-mono, monospace)', lineHeight: '1.5' }}>
                    <div>input_mode: {String(redline.global_diagnostics.input_mode)}</div>
                    <div>extraction_method: {String(redline.global_diagnostics.extraction_method)}</div>
                    <div>cover_extracted_char_count: {String(redline.global_diagnostics.cover_extracted_char_count)}</div>
                    <div>body_extracted_char_count: {String(redline.global_diagnostics.body_extracted_char_count)}</div>
                  </div>
                </details>
              )}

              {redline.download_url && (
                <div style={{ marginTop: '16px' }}>
                  <a href={redline.download_url} target="_blank" rel="noopener noreferrer"
                    style={{
                      display: 'inline-block',
                      padding: '10px 20px', borderRadius: '4px',
                      background: '#b87333', color: '#fff',
                      fontSize: '13px', fontWeight: 600, textDecoration: 'none',
                      fontFamily: 'var(--font-body)',
                    }}>
                    Download Word redline (.docx)
                  </a>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div style={{ padding: '10px 14px', background: '#fef2f2', border: '1px solid #fecaca',
          borderRadius: '4px', color: '#991b1b', fontSize: '13px', marginTop: '12px' }}>
          {error}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
};

export default TaxDisclosure;
